import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface DoubleRoomSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const DoubleRoomSection: React.FC<DoubleRoomSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#double-rooms`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Double Rooms with comfortable accommodation and Alpine views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.doubleRoomBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.doubleRoomBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.doubleRoomBadges.bedding"),
      description: "Comfortable king-size bed",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.doubleRoomBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  // Amenities for text badges
  const amenityBadges = [
    "Perfect for couples",
    "With/without balcony; mountain, lake, or village views",
  ];

  return (
    <section
      id="double-rooms"
      className="py-16 bg-gradient-to-b from-gray-50/50 to-white relative overflow-hidden"
      data-oid="lkhk9hf"
    >
      {/* Background Quadrilaterals - Mirrored positions */}
      <div
        className="absolute top-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="hgpg2tv"
      ></div>
      <div
        className="absolute bottom-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="-_34h1a"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="f5ki7f3"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="u_8z.zy">
          <div className="mb-4" data-oid="xjjmcy5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="6uybxbd"
            >
              {t("rooms.premiumDouble")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="yqk6qdi"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="e0116bp"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="-01lbc."
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="qufkqu7"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="uxpnthc"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="o:mq7k6"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="53l0x3q"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="k8fjo51"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="zdrp-rc"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="rd071t6"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Content Left (40-50%), Carousel Right (50-60%) - MIRRORED */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[45%_55%] 2xl:grid-cols-[40%_60%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="clu0s:j"
        >
          {/* Left Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pl-4 lg:pl-6 xl:pl-8 order-2 lg:order-1"
            data-oid="kohzu1p"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="prrvsgr">
              <div className="mb-4" data-oid="p8vgsw0">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="kqg4xum"
                >
                  {t("rooms.premiumDouble")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="ezs4w2q"
              >
                <div className="flex items-center gap-2" data-oid="it5e4i3">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="s5s.ewa"
                  />

                  <span className="text-sm text-gray-900" data-oid="m:xby77">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="dlu0g32">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="tyo0uba"
                  />

                  <span className="text-sm text-gray-900" data-oid="ijhp27h">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="bwa6p8w">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="rv2wxpy"
                  />

                  <span className="text-sm text-gray-900" data-oid="f.vpbso">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="2ytcgzn">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="j9ogmo:"
              >
                <span className="sm:hidden" data-oid="s9.zo4r">
                  Comfortable double rooms with flexible bedding options,
                  perfect for couples or business travelers. Choose between
                  mountain views of Eiger, Mönch & Jungfrau or Lake Thun views.
                </span>
                <span className="hidden sm:inline" data-oid=":jtnlha">
                  {t("rooms.doubleRoomDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="l_orshp">
              {/* Perfect for couples */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="lwu50xh"
              >
                <Users
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid=".4uti7i"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="c70ih6w"
                >
                  {t("rooms.doubleRoomFeatures.perfectForCouples")}
                </span>
              </div>

              {/* With/without balcony; mountain/lake or village views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="5m-:v.o"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="j5gr4-3"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="l5cr7c7"
                >
                  {t("rooms.doubleRoomFeatures.balconyWithViews")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="_bpjofb"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="o0b.aon"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="zpfwue-"
                >
                  Free Wifi
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="btibwq7"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="fyw-kg2"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="nak49dx"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="reianc0"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="u:m:0io"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="27369sv"
                >
                  Ensuite coffee machine
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="ir0fg0-"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Double Room Section"
                data-oid="w1z-2w0"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="7ey61pr"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Premium Double Rooms"
                data-oid="ssimiq5"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="bdsieiw" />
                {t("common.share")}
              </Button>
            </div>
          </div>

          {/* Right Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden rounded-2xl shadow-2xl order-1 lg:order-2"
            data-oid="mpm7ugq"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="y1nomsx"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="03341x8"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin="200px"
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="l725kjh"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="up0a_km"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="s-.duaf" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="gxyp8hj"
                >
                  <ChevronRight className="w-5 h-5" data-oid="w.z:5hv" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="3p_vwyf"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="uvqkar:"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DoubleRoomSection;
